export type VegaExternalSourceType = 'slack' | 'confluence' | 'jira' | 'github' | 'atlas';

export interface VegaExternalSourceMatch {
  url: string;
  sourceType: VegaExternalSourceType;
}

/**
 * Detect external source URLs that Vega cites in messages — Slack, Confluence, Jira,
 * GitHub, Atlas. These should open a new Vega conversation seeded with the URL as
 * context rather than navigating away in a new tab.
 */
export function matchVegaExternalSourceUrl(href: string | undefined): VegaExternalSourceMatch | null {
  if (!href) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(href);
  } catch {
    return null;
  }

  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname;

  if (host === 'slack.com' || host.endsWith('.slack.com')) {
    return { url: href, sourceType: 'slack' };
  }
  if (host === 'github.com' || host.endsWith('.github.com')) {
    // Pull request and issue URLs are outputs Vega has already produced (for example,
    // a fix PR it filed) — they should open in a new tab like any other external link,
    // not start a new conversation. Only non-PR/non-issue GitHub URLs are treated as
    // sources to investigate.
    if (/^\/[^/]+\/[^/]+\/(pull|issues)\//.test(path)) {
      return null;
    }
    if (/^\/(login|apps|settings)(\/|$)/.test(path)) {
      return null;
    }
    return { url: href, sourceType: 'github' };
  }
  if (host.endsWith('.atlassian.net')) {
    if (path.startsWith('/wiki/')) {
      return { url: href, sourceType: 'confluence' };
    }
    if (path.startsWith('/browse/')) {
      return { url: href, sourceType: 'jira' };
    }
  }
  if (host === 'atlas.atlassian.com' || host.endsWith('.atlas.atlassian.com')) {
    return { url: href, sourceType: 'atlas' };
  }

  return null;
}
